import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
    FileDown, RefreshCcw, Eye, Download, CheckCircle2, Archive as ArchiveIcon,
    Send, Shield, Star, ArrowUpRight,
} from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";

const STATUS_META = {
    draft: { label: "Draft", tone: "bg-gray-200 text-gray-700" },
    under_review: { label: "Under Review", tone: "bg-amber-100 text-amber-800" },
    approved: { label: "Approved", tone: "bg-emerald-100 text-emerald-800" },
    published: { label: "Published", tone: "bg-[color:var(--gsf-gold-500)]/25 text-[color:var(--gsf-green-700)]" },
    archived: { label: "Archived", tone: "bg-slate-100 text-slate-500" },
};

// Namespace prefix for any bespoke class names — prevents style leakage
// into the public site per the isolation requirements.
const NS = "impact-snapshot";

export default function AdminImpactSnapshot() {
    const [versions, setVersions] = useState([]);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        version_number: "1.0",
        reporting_period: "",
        featured_slug: "zimshine-community-enterprise",
        notes: "",
    });

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/impact-snapshot/versions");
            setVersions(data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    async function generate() {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/impact-snapshot/generate", form);
            toast.success(`Draft ${data.version_id.slice(0, 8)}… created (${(data.size_bytes / 1024).toFixed(0)} KB)`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function setStatus(v, status) {
        try {
            await api.post(`/admin/impact-snapshot/${v.version_id}/status`, { status });
            toast.success(`Version v${v.version_number} → ${STATUS_META[status].label}`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    const published = versions.find((v) => v.status === "published");
    const publicUrl = `${BACKEND_URL}/api/public/impact-snapshot.pdf`;

    return (
        <div data-testid="admin-impact-snapshot" className={`${NS}-root`}>
            <div className="gsf-eyebrow">Reporting</div>
            <h1 className="mt-2 font-serif text-3xl text-[color:var(--gsf-green-700)] font-bold">Community Impact Snapshot</h1>
            <p className="mt-3 text-sm text-gray-600 max-w-3xl">
                Executive-grade PDF built from verified CMS data. Generate a draft, review it, mark it{" "}
                <em>Approved</em>, then <em>Publish</em> — only the latest published version is served publicly.
            </p>

            {/* --- Currently published --- */}
            <section className="mt-8 gsf-card p-6" data-testid="snapshot-published-card">
                <div className="flex flex-wrap gap-4 items-start justify-between">
                    <div>
                        <div className="gsf-eyebrow flex items-center gap-1"><Shield size={12} /> Currently public</div>
                        {published ? (
                            <>
                                <div className="mt-2 font-serif text-xl text-[color:var(--gsf-green-700)] font-semibold">
                                    v{published.version_number} · {published.reporting_period}
                                </div>
                                <div className="mt-1 text-xs text-gray-500">
                                    Approved by <b>{published.approved_by || "—"}</b> · {published.approved_at?.slice(0, 16).replace("T", " ")}
                                    {" · "}
                                    {(published.size_bytes / 1024).toFixed(0)} KB · {published.download_count || 0} downloads
                                </div>
                            </>
                        ) : (
                            <div className="mt-2 text-sm text-gray-500">No version has been published yet — the public endpoint will auto-generate on first request.</div>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="gsf-btn-outline text-sm" data-testid="snapshot-preview-public">
                            <Eye size={14} /> Preview public URL
                        </a>
                        <a href={publicUrl} download className="gsf-btn-gold text-sm" data-testid="snapshot-download-public">
                            <FileDown size={14} /> Download public PDF
                        </a>
                    </div>
                </div>
            </section>

            {/* --- Generate a new draft --- */}
            <section className="mt-6 gsf-card p-6" data-testid="snapshot-new-draft-card">
                <div className="gsf-eyebrow">Generate a new draft</div>
                <h2 className="mt-1 font-serif text-lg text-[color:var(--gsf-green-700)] font-semibold">Pull the latest verified data into a fresh PDF</h2>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        placeholder="Version (e.g. 1.1)"
                        value={form.version_number}
                        onChange={(e) => setForm({ ...form, version_number: e.target.value })}
                        data-testid="snapshot-form-version"
                    />
                    <input
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        placeholder="Reporting period (e.g. Q1 2026)"
                        value={form.reporting_period}
                        onChange={(e) => setForm({ ...form, reporting_period: e.target.value })}
                        data-testid="snapshot-form-period"
                    />
                    <input
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        placeholder="Featured project slug"
                        value={form.featured_slug}
                        onChange={(e) => setForm({ ...form, featured_slug: e.target.value })}
                        data-testid="snapshot-form-featured"
                    />
                    <input
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        placeholder="Internal notes (optional)"
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        data-testid="snapshot-form-notes"
                    />
                </div>
                <button
                    onClick={generate}
                    disabled={busy}
                    className="mt-4 gsf-btn-primary text-sm disabled:opacity-60"
                    data-testid="snapshot-generate-btn"
                >
                    <RefreshCcw size={14} className={busy ? "animate-spin" : ""} />
                    {busy ? "Generating…" : "Generate draft"}
                </button>
            </section>

            {/* --- Version archive --- */}
            <section className="mt-8" data-testid="snapshot-archive-section">
                <div className="gsf-eyebrow flex items-center gap-1"><ArchiveIcon size={12} /> Version archive</div>
                <h2 className="mt-1 font-serif text-lg text-[color:var(--gsf-green-700)] font-semibold">
                    All versions ({versions.length})
                </h2>
                <div className="mt-4 gsf-card overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#f8faf7] text-left text-xs">
                            <tr>
                                <th className="p-3">Version</th>
                                <th className="p-3">Reporting period</th>
                                <th className="p-3">Generated</th>
                                <th className="p-3">By</th>
                                <th className="p-3">Status</th>
                                <th className="p-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {versions.length === 0 ? (
                                <tr><td colSpan="6" className="p-8 text-center text-gray-500">No versions yet — generate a draft above.</td></tr>
                            ) : versions.map((v) => {
                                const meta = STATUS_META[v.status] || STATUS_META.draft;
                                const downloadUrl = `${BACKEND_URL}/api/admin/impact-snapshot/${v.version_id}/download`;
                                return (
                                    <tr key={v.version_id} className="border-t border-gray-100" data-testid={`snapshot-row-${v.version_id}`}>
                                        <td className="p-3 font-medium text-[color:var(--gsf-green-700)]">v{v.version_number}</td>
                                        <td className="p-3">{v.reporting_period || "—"}</td>
                                        <td className="p-3 text-xs">{v.generated_at?.slice(0, 16).replace("T", " ")}</td>
                                        <td className="p-3 text-xs text-gray-600">{v.generated_by || "—"}</td>
                                        <td className="p-3">
                                            <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-semibold ${meta.tone}`}>
                                                {v.status === "published" && <Star size={10} />}
                                                {meta.label}
                                            </span>
                                        </td>
                                        <td className="p-3">
                                            <div className="flex flex-wrap gap-1.5">
                                                <a href={downloadUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50" data-testid={`snapshot-preview-${v.version_id}`}>
                                                    <Eye size={12} /> Preview
                                                </a>
                                                {v.status === "draft" && (
                                                    <button onClick={() => setStatus(v, "under_review")} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-amber-100 text-amber-800 hover:bg-amber-200" data-testid={`snapshot-review-${v.version_id}`}>
                                                        Submit for review
                                                    </button>
                                                )}
                                                {(v.status === "draft" || v.status === "under_review") && (
                                                    <button onClick={() => setStatus(v, "approved")} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200" data-testid={`snapshot-approve-${v.version_id}`}>
                                                        <CheckCircle2 size={12} /> Approve
                                                    </button>
                                                )}
                                                {v.status === "approved" && (
                                                    <button onClick={() => setStatus(v, "published")} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-[color:var(--gsf-green-700)] text-white hover:bg-[color:var(--gsf-green-800)]" data-testid={`snapshot-publish-${v.version_id}`}>
                                                        <ArrowUpRight size={12} /> Publish
                                                    </button>
                                                )}
                                                {v.status === "published" && (
                                                    <button onClick={() => setStatus(v, "archived")} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 hover:bg-slate-200" data-testid={`snapshot-archive-${v.version_id}`}>
                                                        <ArchiveIcon size={12} /> Archive
                                                    </button>
                                                )}
                                                {v.status === "archived" && (
                                                    <button onClick={() => setStatus(v, "approved")} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 hover:bg-slate-200" data-testid={`snapshot-restore-${v.version_id}`}>
                                                        Restore
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* --- Stakeholder distribution scaffold --- */}
            <section className="mt-10 gsf-card p-6" data-testid="snapshot-stakeholder-scaffold">
                <div className="gsf-eyebrow flex items-center gap-1"><Send size={12} /> Stakeholder distribution</div>
                <h2 className="mt-1 font-serif text-lg text-[color:var(--gsf-green-700)] font-semibold">
                    Send the latest approved snapshot to partners
                </h2>
                <p className="mt-3 text-sm text-gray-600 max-w-2xl">
                    Distribution is installed but <b>automatic sending is disabled</b>. Add stakeholders via
                    <code className="mx-1 px-1 rounded bg-gray-100 text-xs">POST /api/admin/stakeholders</code>,
                    then run a manually-approved campaign. Set
                    <code className="mx-1 px-1 rounded bg-gray-100 text-xs">STAKEHOLDER_AUTO_SEND=true</code> in
                    backend env when ready to activate scheduled runs.
                </p>
                <p className="mt-3 text-xs text-amber-700">
                    Consent, unsubscribe management, bounce handling and audit logging are enforced before any send.
                </p>
            </section>
        </div>
    );
}
